import { z } from 'zod';
import { TenantIdSchema } from './common.js';

/*
 * Tenant da edição Cloud: um cliente, com endereço próprio (<cliente>.<domínio>/mcp), o IdP que ele
 * aceita e as conexões com as fontes. Segredos ficam cifrados com a chave do tenant (kms:...).
 */

/** Referência a segredo cifrado pelo serviço de chaves do tenant. */
export const KmsRefSchema = z.string().regex(/^kms:v1:[A-Za-z0-9_-]+:[A-Za-z0-9+/=]+$/, 'segredo cifrado no formato kms:v1:<chave>:<dados>');

export const TenantIdentitySchema = z.discriminatedUnion('kind', [
  /** App multi-tenant da Cluster no Entra: o tenant registra qual emissor aceita. */
  z.object({ kind: z.literal('entra'), entra_tenant_id: z.string().uuid(), user_claim: z.string().default('upn'), allow_guests: z.boolean().default(false) }),
  /** OIDC genérico (v2): descoberta pelo issuer. */
  z.object({ kind: z.literal('oidc'), issuer: z.string().url(), audience: z.string().min(1), user_claim: z.string().default('email'), roles_claim: z.string().default('roles') }),
  /** JWT assinado pelo sistema do próprio cliente (portal, intranet). */
  z.object({
    kind: z.literal('jwt'),
    issuer: z.string().min(1),
    audience: z.string().min(1),
    jwks_uri: z.string().url().nullable().default(null),
    public_key_pem: z.string().nullable().default(null),
    user_claim: z.string().default('sub'),
    roles_claim: z.string().default('roles'),
  }),
]);
export type TenantIdentity = z.infer<typeof TenantIdentitySchema>;

export const TenantSchema = z.object({
  id: TenantIdSchema.refine((t) => t !== 'default', 'default é reservado ao Self-hosted'),
  nome: z.string().min(1).max(200),
  /** Hosts que respondem por este tenant (ex.: cliente.lastrouter.example). */
  hosts: z.array(z.string().regex(/^[a-z0-9.-]+$/)).min(1),
  status: z.enum(['ativo', 'suspenso']),
  identity: TenantIdentitySchema,
  /** Fontes do tenant no mesmo formato de sources[] do lastrouter.yaml, com segredos kms:. */
  sources: z.array(z.record(z.string(), z.unknown())).default([]),
  region: z.string().min(1).default('br'),
  /** false: neste tenant, query e describe_app ficam só no catálogo (ADR 0037). Ausente: vale model_fallback.enabled. */
  model_fallback: z.boolean().optional(),
  created_at: z.string(),
});
export type Tenant = z.infer<typeof TenantSchema>;
